/*----------------------------------
- SALES PAGE CONTENT CATALOG (SSOT)
----------------------------------*/

/**
 * @docs docs/features/sales-page
 * @adr  ADR-0067
 * @rule The website's words for `/sales`: the shared page copy (`SALES_PAGE_DEFAULT_COPY`, one
 *       wording with Product), plus the meta, the persona card, the lookup card, the mobile
 *       action, the Ask Your AI facts and the Markdown mirror's headings; the walls' bodies live
 *       with every other wall (`accountCopy.ts`). No string names where a sale was reported, none
 *       says "unlock", and none carries an em dash.
 */

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import { SALES_PAGE_DEFAULT_COPY } from '@uniquedomains/components/sales/salesCopy';

/*----------------------------------
- CATALOG (SSOT)
----------------------------------*/

export const SALES_PAGE_CONTENT = {
    meta: {
        pageTitle: 'Domain sales: reported sales, medians by ending and word length',
        metaDescription:
            'Reported domain sales, updated each night: the newest sales with their price and what the name costs today, medians by ending and by word length, and the words selling more than they did a month ago.',
        markdownTitle: 'Domain sales (Markdown)',
        breadcrumb: 'Domain sales',
    },
    unavailable: 'The sales page is being prepared. Please try again shortly.',
    copy: SALES_PAGE_DEFAULT_COPY,
    personas: {
        heading: 'Watch the words that sell, or name what you build',
        body: 'Investors hear the morning after a sale lands near their words. Founders see what a word sells for before they pick a name.',
    },
    mobileAction: 'Alert me on a word',
    newsletter: {
        heading: 'The domain market in your inbox, every week',
        body: 'The Monday brief reports the week in domains, sales included. Free, no account needed.',
    },
    relatedLinksTitle: 'Related pages',
    lookup: {
        result: (word: string) => `${word}: the last 12 months`,
        sales: (count: string) => `${count} usable sales`,
        median: (median: string) => `median ${median}`,
        endings: (count: number) => `${count} ending${count === 1 ? '' : 's'} sold on`,
        lastSale: (month: string) => `last sale ${month}`,
        none: (word: string) => `No usable sale of ${word} in the archive yet.`,
        invalid: 'Type one word, the way a domain carries it.',
        cta: 'Every sale of this word',
    },
    /** The prompt's figures, one line each, from the day's aggregates (`buildSalesAskAiFacts`). */
    askAi: {
        window: 'Usable sales over {window}: {count}, median {median} ({trend} on the previous {window})',
        busiest: 'Busiest ending over 7 days: {ending}, {share}% of the sales',
        updated: 'Updated {rollup}',
    },
    /** `/sales.md`: its headings, table heads and sentences. Figures come from the page bundle. */
    mirror: {
        pending: 'The first nightly sales rollup has not run yet.',
        updated: 'Updated {rollup}. {total} usable sales in the archive{since}, {endings} endings with a sale in 12 months.',
        since: ' since {day}',
        busiest: 'The busiest ending over 7 days is {ending}, with {share}% of the sales.',
        method: 'Method',
        windows: { title: 'Sales by window (as of {day})', heads: ['Window', 'Sales', 'Previous', 'Trend', 'Median', 'Previous median'] },
        bands: { title: 'Price bands', heads: ['Band', '30 days', '12 months'] },
        endings: {
            title: 'Sales by ending',
            heads: ['Ending', 'Sales 12 months', 'Median 12 months', 'Previous 12 months', 'Previous median', 'Trend', 'Dictionary words', '$1,000 or more'],
        },
        lengths: { title: 'Sales by word length on {ending}', heads: ['Letters', 'Sales 12 months', 'Median 12 months', 'Previous median', 'Trend', 'Sales 30 days'] },
        daily: { title: 'Daily history', heads: ['Day', 'Sales', 'Median', 'Dictionary words', '.com', '.ai'] },
        weekly: { title: 'Weekly history', heads: ['Week of', 'Sales', 'Median'] },
        catalog: {
            title: 'What the catalog says about the names sold',
            dictionary: '- Dictionary words: {share30d} of sales over 30 days, {share12m} over 12 months.',
            openElsewhere: '- Dictionary words still open on another ending: {share30d} over 30 days, {share12m} over 12 months.',
            tracked: '- Sold names the catalog tracks: {count30d} over 30 days, {count12m} over 12 months.',
            comMedians: '- .com medians over 12 months: {dictionary} for dictionary words, {other} for other names.',
        },
        movers: { title: 'Word movers', heads: ['Word', 'Sales 30 days', 'Previous 30 days', 'Sales 90 days', 'Median 12 months', 'Endings sold on'] },
        soldBefore: {
            title: 'Sold before, buyable today',
            text: '{size} catalog names with a past usable sale can be registered, bought or caught today; {today} joined the list today. The names are listed in the product.',
        },
        cite: 'Cite this page',
        related: 'Related pages',
    },
} as const;
